import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import BackButton from '../components/ui/atoms/BackButton';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import fetchWithAuth from '../utils/fetchWithAuth';

// Feedback types
type CommentType = 'bug' | 'feature' | 'comment';

type FormData = {
  email: string;
  title: string;
  description: string;
  extra: string;
};

const SettingsFeedback: React.FC = () => {
  const [type, setType] = useState<CommentType | null>(null);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    title: '',
    description: '',
    extra: '',
  });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth() as any;
  const navigate = useNavigate();
  const { showToast } = useToast() as any;

  const handleSubmit = async () => {
    if (!type) return;
    setLoading(true);
    try {
      // send to your new Express API
      const payload = {
        type,
        title: formData.title || null,
        description: formData.description,
        extra:
          type === 'bug'
            ? `${formData.extra}\n\n---\nBrowser: ${navigator.userAgent}\nViewport: ${window.innerWidth}×${window.innerHeight}`
            : formData.extra || null,
      };
      
      // fetchWithAuth will throw on non-2xx, so just await it:
      await fetchWithAuth('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const label =
        type === 'bug'
          ? 'Bug report'
          : type === 'feature'
            ? 'Feature request'
            : 'Feedback';

      showToast({
        message: `${label} submitted successfully!`,
        type: 'success',
      });
      navigate('/app/settings');
    } catch (error) {
      console.error('Feedback submit error:', error);
      showToast({
        message: 'Failed to submit. Please try again.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const commonInput = (
    label: string,
    field: keyof FormData,
    placeholder: string
  ) => (
    <div className="space-y-1">
      <label className="block text-sm text-gray-500">{label}</label>
      <input
        type="text"
        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring"
        value={formData[field]}
        onChange={(e) => handleChange(field, e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );

  const textareaInput = (
    label: string,
    field: keyof FormData,
    placeholder: string
  ) => (
    <div className="space-y-1">
      <label className="block text-sm text-gray-500">{label}</label>
      <textarea
        rows={4}
        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring"
        value={formData[field]}
        onChange={(e) => handleChange(field, e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div className="h-full bg-white flex flex-col">
      <header className="flex items-center px-4 py-3">
        <BackButton onClick={() => navigate('/app/settings')} />
        {/* <h1 className="text-lg font-medium ml-3">Feedback & Reports</h1> */}
      </header>

      <main className="flex-grow px-2 pb-4 space-y-6 overflow-auto">
        <section className="bg-white rounded-xl divide-y divide-gray-200 overflow-hidden">
          {!type ? (
            <div className="p-4 space-y-4">
              <button
                onClick={() => setType('bug')}
                className="w-full text-left p-4 bg-red-50 rounded-lg hover:bg-red-100"
              >
                Report a Bug
              </button>
              <button
                onClick={() => setType('feature')}
                className="w-full text-left p-4 bg-blue-50 rounded-lg hover:bg-blue-100"
              >
                Request a Feature
              </button>
              <button
                onClick={() => setType('comment')}
                className="w-full text-left p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                Leave Feedback
              </button>
            </div>
          ) : (
            <form className="p-4 space-y-6">
              {(type === 'bug' || type === 'feature') &&
                commonInput(
                  type === 'bug' ? 'Title' : 'Feature Title',
                  'title',
                  type === 'bug' ? 'Bug summary…' : 'Feature title…'
                )}

              {textareaInput(
                type === 'comment'
                  ? 'Feedback'
                  : type === 'bug'
                    ? 'Describe the bug'
                    : 'Describe the feature',
                'description',
                type === 'comment'
                  ? 'Anything on your mind…'
                  : type === 'bug'
                    ? 'Steps to reproduce…'
                    : 'How would this help?'
              )}

              {type !== 'comment' &&
                textareaInput(
                  type === 'bug'
                    ? 'Expected Behavior / Environment'
                    : 'Why is this useful?',
                  'extra',
                  type === 'bug'
                    ? 'What should’ve happened, browser…'
                    : 'Any reasoning…'
                )}

              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className={`w-full rounded-full py-3 font-medium text-white ${
                  loading ? 'bg-gray-300' : 'bg-primary hover:bg-green-600'
                }`}
              >
                {loading ? 'Submitting…' : 'Submit'}
              </button>

              <button
                type="button"
                onClick={() => setType(null)}
                className="w-full text-center text-sm text-gray-500"
              >
                Cancel
              </button>
            </form>
          )}
        </section>
      </main>
    </div>
  );
};

export default SettingsFeedback;
